import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Header } from '@/components/layout/header/Header';
import { useCustomAuth } from '@/contexts/CustomAuthContext';
import { toast } from 'sonner';
import { format, isValid, parse } from 'date-fns';
import { Filter, RefreshCw } from 'lucide-react';
import { PeriodPicker } from '@/components/ui/period-picker';
import userCostAnalysisService from './userCostAnalysisService';
import UserCostSummary from './UserCostSummary';
import UserCostCharts from './UserCostCharts';
import UserCostTable from './UserCostTable';
import UserCostDetailModal from './UserCostDetail';
import type { UserCostReport } from './userCostAnalysisService';

const DAY_FORMAT = 'yyyy-MM-dd';

/** 'yyyy-MM-dd' → meia-noite local. `new Date('yyyy-MM-dd')` leria como UTC. */
function parseDay(value: string): Date | undefined {
  if (!value) return undefined;
  const date = parse(value, DAY_FORMAT, new Date());
  return isValid(date) ? date : undefined;
}

function getDefaultRange() {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  // `format` usa o dia local; `toISOString()` daria o dia UTC da meia-noite local.
  return {
    start: format(firstDay, DAY_FORMAT),
    end: format(lastDay, DAY_FORMAT),
  };
}

export default function UserCostAnalysisPage() {
  useLanguage();
  const { user } = useCustomAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.nivel !== 'admin') {
      toast.error(translateInterfaceText("Área restrita a administradores."));
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const [startDate, setStartDate] = useState(getDefaultRange().start);
  const [endDate, setEndDate] = useState(getDefaultRange().end);

  const [report, setReport] = useState<UserCostReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const fetchReport = useCallback(async (start: string, end: string) => {
    setLoading(true);
    try {
      const data = await userCostAnalysisService.getReport(start, end);
      setReport(data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao carregar relatório de custos por usuário."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReport(startDate, endDate);
  }, []);

  const handleApply = () => {
    fetchReport(startDate, endDate);
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      const result = await userCostAnalysisService.syncSummary(startDate, endDate);
      toast.success(translateInterfaceText("Resumo de métricas consolidado: {value0} dias processados.", { value0: (result.daysProcessed) }));
      fetchReport(startDate, endDate);
    } catch (err: any) {
      toast.error(err.response?.data?.message || translateInterfaceText("Erro ao consolidar resumo de métricas."));
    } finally {
      setSyncing(false);
    }
  };

  const handleUserClick = (userId: string) => {
    setSelectedUserId(userId);
    setDetailOpen(true);
  };

  const handleDetailClose = () => {
    setDetailOpen(false);
    setSelectedUserId(null);
  };

  if (!user || user.nivel !== 'admin') return null;

  return (
    <Layout fixedSidebar={true}>
      <div className="flex-1 flex flex-col mobile-scroll-fix">
        <Header title={translateInterfaceText("Análise de Custos por Usuário")} />
        <div className="hidden lg:block h-px bg-border w-full" />
        <main className="flex-1 lg:overflow-y-auto">
          <div className="container mx-auto px-4 md:px-6 py-6 md:py-8 pb-[calc(120px+env(safe-area-inset-bottom))] lg:pb-28 space-y-6 max-w-6xl">

            {/* BANNER */}
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
              <div>
                <h2 className="text-md font-bold text-slate-900">{translateInterfaceText("Análise de Custos por Usuário")}</h2>
                <p className="text-xs text-slate-400 mt-0.5">{translateInterfaceText("Distribuição proporcional dos custos de infraestrutura por usuário com base em métricas de consumo (retenção, plays, vídeos).")}</p>
              </div>
            </div>

            {/* FILTERS */}
            <div className="bg-white border border-slate-100 rounded-2xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-slate-50">
                <Filter size={14} className="text-blue-600" />
                <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Filtros de Período")}</span>
              </div>
              <div className="flex flex-col sm:flex-row items-end gap-3">
                <div className="flex-1 w-full">
                  <label className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider block mb-1.5">{translateInterfaceText("Período")}</label>
                  {/* O padrão é o mês corrente inteiro, que termina no futuro. */}
                  <PeriodPicker
                    value={{ from: parseDay(startDate), to: parseDay(endDate) }}
                    onChange={({ from, to }) => {
                      setStartDate(format(from, DAY_FORMAT));
                      setEndDate(format(to, DAY_FORMAT));
                    }}
                    disableFuture={false}
                    className="h-[38px] min-h-0 md:min-w-[240px]"
                  />
                </div>
                <button
                  onClick={handleApply}
                  className="flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-xl text-xs font-semibold shadow-sm transition-all active:scale-[0.99] shrink-0 h-[38px]"
                >
                  <Filter size={13} /> {" "}{translateInterfaceText("Aplicar")}</button>
                <button
                  onClick={handleSync}
                  disabled={syncing || loading}
                  title={translateInterfaceText("Consolida o resumo diário de métricas do período (dia de hoje é lido ao vivo)")}
                  className="flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-600 px-4 py-2 rounded-xl text-xs font-semibold transition-all active:scale-[0.99] shrink-0 h-[38px] disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <RefreshCw size={13} className={syncing ? 'animate-spin' : ''} /> {" "}{translateInterfaceText("Sincronizar Métricas")}</button>
              </div>
            </div>

            {/* SUMMARY */}
            <UserCostSummary report={report} loading={loading} />

            {/* CHARTS */}
            <UserCostCharts report={report} loading={loading} onUserClick={handleUserClick} />

            {/* TABLE */}
            <UserCostTable
              users={report?.users || []}
              loading={loading}
              onUserClick={handleUserClick}
            />

            {/* DETAIL MODAL */}
            <UserCostDetailModal
              isOpen={detailOpen}
              onClose={handleDetailClose}
              userId={selectedUserId}
              startDate={startDate}
              endDate={endDate}
            />
          </div>
        </main>
      </div>
    </Layout>
  );
}
